import { ScrollToTop } from "@/components/common/ScrollToTop";
import { paths } from "@/components/examinee/interview/Path";
import { useSleepCheckupInfoAPI } from "@/components/examinee/sleep_checkup/SleepCheckupAPI";
import { SleepCheckupInfo } from "@/components/examinee/sleep_checkup/Types";
import { Button, LinearProgress, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

// このページが受け取るstateの型
export type StateType = {
  sleepCheckupUuid: string;
};

export const RegistrationCompleted = () => {
  const location = useLocation();

  const { sleepCheckupUuid } = (location.state as StateType) || {};

  const api = useSleepCheckupInfoAPI();
  const [info, setInfo] = useState<SleepCheckupInfo | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!sleepCheckupUuid) {
      setLoaded(true);
      return;
    }
    api
      .get(sleepCheckupUuid)
      .then((res) => setInfo(res.data))
      // 取得失敗時は info=null のまま現行表示にフォールバックする
      // エラーは画面内で扱わないため、ここでは握りつぶす
      .catch(() => setInfo(null))
      .finally(() => setLoaded(true));
  }, [api, sleepCheckupUuid]);

  // 「回答済み」判定は PrimaryInterviewEntrance と同一基準
  // ※status は 4値 SleepCheckupInfoStatus（8種の ExamineeStatus とは別物）
  const isPrimaryInterviewCompleted =
    info != null &&
    ["primary_interview_completed", "report_sent"].includes(info.status);
  // 出し分けは新フロー限定。旧フロー・判定不能時は現行表示を維持する（#1775）
  const isNewFlow = info?.is_new_flow ?? false;

  return (
    <>
      <ScrollToTop />
      <LinearProgress
        variant="determinate"
        value={100}
        sx={{
          borderRadius: 1,
          my: 6,
          mx: 4,
          height: "6px",
        }}
      />
      <Stack spacing={8} sx={{ mt: 8, mb: 12, mx: 4 }}>
        <Stack spacing={6} alignItems="center">
          <img
            src="/img/image_registration_completed_01.png"
            alt="登録完了"
            loading="lazy"
            style={{ width: 260, height: "100%" }}
          />
          <Typography variant="h6">登録完了</Typography>
        </Stack>
        {/* 誤案内のちらつき防止のため、本文・ボタンは取得完了後に描画する */}
        {loaded &&
          (isNewFlow ? (
            isPrimaryInterviewCompleted ? (
              <>
                <Stack>
                  <Typography
                    variant="subtitle2"
                    align="center"
                    sx={{ color: "text.secondary" }}
                  >
                    デバイスIDの登録が完了しました
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    align="center"
                    sx={{ color: "text.secondary" }}
                  >
                    続いて「測定期間」をご登録ください。
                    測定期間を登録すると、測定開始後の毎朝、毎日問診の回答用メールが届きます。
                  </Typography>
                </Stack>

                <Button
                  component={Link}
                  to={
                    sleepCheckupUuid
                      ? `/examinee/measurement_period/${sleepCheckupUuid}`
                      : "/examinee/signup"
                  }
                  variant="contained"
                  size="large"
                  disabled={!sleepCheckupUuid}
                >
                  測定期間を登録する
                </Button>
              </>
            ) : (
              <>
                <Stack>
                  <Typography
                    variant="subtitle2"
                    align="center"
                    sx={{ color: "text.secondary" }}
                  >
                    デバイスIDの登録が完了しました
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    align="center"
                    sx={{ color: "text.secondary" }}
                  >
                    続いて「事前問診」にご回答ください
                  </Typography>
                </Stack>

                <Button
                  component={Link}
                  to={paths.getFullPath("PrimaryInterviewIntroduction", {
                    ":sleepCheckupId": sleepCheckupUuid,
                  })}
                  variant="contained"
                  size="large"
                >
                  事前問診に回答する
                </Button>
              </>
            )
          ) : (
            <>
              <Stack>
                <Typography
                  variant="subtitle2"
                  align="center"
                  sx={{ color: "text.secondary" }}
                >
                  以上でアカウント登録は完了です
                </Typography>
                <Typography
                  variant="subtitle2"
                  align="center"
                  sx={{ color: "text.secondary" }}
                >
                  続いて「事前問診」にご回答ください
                </Typography>
              </Stack>

              <Button
                component={Link}
                to={paths.getFullPath("PrimaryInterviewIntroduction", {
                  ":sleepCheckupId": sleepCheckupUuid,
                })}
                variant="contained"
                size="large"
              >
                事前問診に回答する
              </Button>
            </>
          ))}
      </Stack>
    </>
  );
};
